import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';

import { formatCentsPrice } from 'services/helpers/formatPrice';

import DetailsSectionCard, { SECTION_BORDER } from './DetailsSectionCard';
import Pill from './Pill';
import formatDate from './formatDate';

const useStyles = makeStyles((theme) => ({
  grid: {
    padding: theme.spacing(1, 3, 2),
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    columnGap: theme.spacing(3),
  },

  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: theme.spacing(1.5, 0),
    borderBottom: SECTION_BORDER,
  },

  lastRow: {
    borderBottom: 'none',
  },

  wide: {
    gridColumn: 'span 2',
  },

  label: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },

  value: {
    fontSize: 16,
    fontWeight: 'bold',
  },

  warning: {
    color: '#B4530A',
  },

  muted: {
    color: theme.palette.secondary.contrastText,
  },

  headerPills: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
}));

interface Props {
  achData: any;
}

const AchRiskProfileSection = ({ achData }: Props) => {
  const classes = useStyles();

  const lastSuccessful = formatDate(achData?.lastSuccessfulClearanceAt);
  const lastUnsuccessful = formatDate(achData?.lastUnsuccessfulClearanceAt);
  const hasUnsettled = Number(achData?.unsettledCount) > 0;

  return (
    <DetailsSectionCard
      id="user-details-ach"
      title="ACH Risk Profile"
      action={
        achData?.riskTier != null && (
          <span className={classes.headerPills}>
            {achData.isDefault && <Pill variant="neutral">Default tier</Pill>}
            <Pill variant={achData.isDefault ? 'neutral' : 'primaryTint'}>
              Tier {achData.riskTier}
            </Pill>
          </span>
        )
      }>
      <div className={classes.grid}>
        <div className={classes.cell}>
          <span className={classes.label}>Instant availability cap</span>
          <span className={classes.value}>
            {achData?.instantAvailabilityCapCents == null
              ? '—'
              : formatCentsPrice(achData.instantAvailabilityCapCents)}
          </span>
        </div>
        <div className={classes.cell}>
          <span className={classes.label}>Instant used amount</span>
          <span className={classes.value}>{formatCentsPrice(achData?.instantUsedAmountCents)}</span>
        </div>
        <div className={classes.cell}>
          <span className={classes.label}>Total ACH amount</span>
          <span className={classes.value}>{formatCentsPrice(achData?.totalAchAmountCents)}</span>
        </div>

        <div className={classes.cell}>
          <span className={classes.label}>Unsettled amount</span>
          <span className={classNames(classes.value, hasUnsettled && classes.warning)}>
            {formatCentsPrice(achData?.unsettledAmountCents)}
          </span>
        </div>
        <div className={classes.cell}>
          <span className={classes.label}>Unsettled count</span>
          <span className={classNames(classes.value, hasUnsettled && classes.warning)}>
            {achData?.unsettledCount ?? '—'}
          </span>
        </div>
        <div className={classes.cell}>
          <span className={classes.label}>Successful clearances</span>
          <span className={classes.value}>{achData?.successfulClearancesCount ?? '—'}</span>
        </div>

        <div className={classNames(classes.cell, classes.lastRow, classes.wide)}>
          <span className={classes.label}>Last successful clearance</span>
          <span className={classNames(classes.value, !lastSuccessful && classes.muted)}>
            {lastSuccessful ?? '—'}
          </span>
        </div>
        <div className={classNames(classes.cell, classes.lastRow)}>
          <span className={classes.label}>Last unsuccessful clearance</span>
          <span className={classNames(classes.value, !lastUnsuccessful && classes.muted)}>
            {lastUnsuccessful ?? '—'}
          </span>
        </div>
      </div>
    </DetailsSectionCard>
  );
};

export default AchRiskProfileSection;
